import { MaintenanceNotice } from './types';

const IMPACT_WEIGHT = { high: 0, medium: 1, low: 2 } as const;

export const isBlockingNotice = (notice: MaintenanceNotice): boolean =>
  notice.state === 'active' && notice.mode === 'block';

/** Blocking first, then active banners, then upcoming. */
const rank = (notice: MaintenanceNotice): number => {
  if (isBlockingNotice(notice)) return 0;
  return notice.state === 'active' ? 1 : 2;
};

/** Orders by rank, then impact (high first), then start time. */
export function orderNotices(notices: readonly MaintenanceNotice[]): MaintenanceNotice[] {
  return [...notices].sort(
    (a, b) =>
      rank(a) - rank(b) ||
      IMPACT_WEIGHT[a.impact] - IMPACT_WEIGHT[b.impact] ||
      Date.parse(a.startsAt) - Date.parse(b.startsAt),
  );
}

export interface PartitionedNotices {
  /** Every active blocking notice in the answer, whole-product ones included. */
  blocking: MaintenanceNotice[];
  /** Active banner notices scoped to exactly `area`. */
  banners: MaintenanceNotice[];
  /** Upcoming (information only) notices scoped to exactly `area`. */
  upcoming: MaintenanceNotice[];
}

/**
 * Splits an answer for `area` (null = site-wide) into what a gate shows. A
 * gate shows only notices scoped to its own area, so the site-wide strip and
 * the page gates never display the same whole-product notice twice; a
 * whole-product block is still reported in `blocking` so every page blocks.
 */
export function partitionNotices(
  notices: readonly MaintenanceNotice[],
  area: string | null,
): PartitionedNotices {
  const ordered = orderNotices(notices);
  return {
    blocking: ordered.filter(isBlockingNotice),
    banners: ordered.filter((n) => n.state === 'active' && n.mode === 'banner' && n.area === area),
    upcoming: ordered.filter((n) => n.state === 'upcoming' && n.area === area),
  };
}

/**
 * When the blocked area should be back: the latest end among the blocking
 * notices, or null if any is open-ended or the time has already passed.
 */
export function expectedBackAt(blocking: readonly MaintenanceNotice[], now: number): string | null {
  if (blocking.length === 0 || blocking.some((n) => n.endsAt === null)) return null;
  const ends = blocking.map((n) => n.endsAt as string);
  const latest = ends.reduce((a, b) => (Date.parse(a) >= Date.parse(b) ? a : b));
  return Date.parse(latest) > now ? latest : null;
}
